// region Imports
import { Component, ViewChild, OnInit, Output, EventEmitter, Input, SimpleChange, OnChanges } from '@angular/core';
import { AfterViewInit } from '@angular/core/src/metadata/lifecycle_hooks';
import { LocatorServiceInterface } from '../../widgets/locator/locator-service-interface.service';
import { ActivatedRoute } from '@angular/router';
import { Grid, PageSettingsModel, TextWrapSettingsModel, SortSettingsModel, VirtualScrollService } from '@syncfusion/ej2-ng-grids';
import { ContentComponent } from '../content/content.component';
import { BoundingBox } from "../../services/pharmacy-locator/models/BoundingBox";
import { BoundingRadius } from "../../services/pharmacy-locator/models/BoundingRadius";
import { Pharmacy } from '../../services/pharmacy-locator/models/pharmacy';
import { PharmacyLocatorService } from '../../services/pharmacy-locator/pharmacy-locator.service';
import { ExternalDependencyLoader } from '../../lib/services/external-dependency-loader.service';
import { MapComponent } from '../../widgets/map/map.component';
import { Location } from '../../lib/maps/location';
import { environment } from '../../environment/environment';
import { LoaderService } from '../../lib/services/loader.service';
import * as L from 'leaflet';
import { Geocode } from '../../services/pharmacy-locator/models/geocode';
import { SettingsService } from "../../lib/services/settings";
import { ApplicationInsightsLogger } from '../../lib/shared/applicationinsights-logger';
import { HttpError } from '../../lib/shared/http-error';
import { async } from '@angular/core/testing';
// endregion

let action: string = 'ascending';

@Component({
    selector: 'pdmi-pharmacy-locator',
    templateUrl: './pharmacy-locator.component.html',
    styleUrls: ['./pharmacy-locator.component.css']
})
export class PharmacyLocatorComponent implements OnInit, OnChanges, AfterViewInit {
    public displayMap: boolean;
    public autoZoom: boolean;
    public minPharmacies: number;
    public initialLoad: boolean = true;

    constructor(
        private content: ContentComponent,
        private pharmacyLocatorService: PharmacyLocatorService,
        private settings: SettingsService,
        private applicationInsightsLogger: ApplicationInsightsLogger,
        private locatorServiceInterface: LocatorServiceInterface,
        private loaderService: LoaderService
    ) {
        this.radiusData = [];
    }

    // region Child Components
    @ViewChild(MapComponent) mapComponent: MapComponent;
    @ViewChild('grid') gridComponent: Grid;
    // endregion

    // region Properties
    selectedNabp: string;
    defaultRadius: any = 10;
    public pageSettings: PageSettingsModel;
    public sortOptions: SortSettingsModel;
    public wrapSettings: TextWrapSettingsModel;
    private pharmacies: Array<Pharmacy>;
    records: any[];
    userLocation: Location;
    searchLocation: Location;
    errorMessage: string;
    error = false;

    @Input() searchAddress = '';
    @Output() contentChanged = new EventEmitter<boolean>();
    // endregion

    radiusData: Array<Object>;

    // region Lifecycle Hooks

    ngOnInit(): void {
        try {
            this.pageSettings = {
                pageCount: 5,
                pageSize: 10
            };
            this.sortOptions = {
                allowUnsort: false,
                columns: [{ field: 'location', direction: 'ascending' }]
            };
            this.wrapSettings = { wrapMode: 'content' };
            this.pharmacyLocatorService.accessToken = this.content.accessToken;

            //this.loaderService.show(true, 'Please wait, loading settings...');
            this.setupMapSettings();


        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    //delay(ms: number) {
    //    return new Promise(resolve => setTimeout(resolve, ms));
    //}

    ngOnChanges(change: { [propKey: string]: SimpleChange }): void {
        try {
            if (change['searchAddress'] !== change['searchAddress'].previousValue) {
                this.updateLocationBySearch();
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    ngAfterViewInit() {
        try {
            this.updateLocationToCurrentPosition();
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }
    // endregion

    // region Event Handlers
    //Grid Region
    onGridActionBegin(e): void {
        try {
            if (e['requestType'] === 'sorting') {
                action = e.direction;
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    onGridActionComplete(e): void {
        try {
            if (e['requestType'] === 'paging') {
                setTimeout(() => {
                    this.content.resize();
                }, 250);
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    onRowSelected(e): void {
        try {
            if (this.displayMap) {
                this.mapComponent.showPopup(e.data.nabp);
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    onSelectedPageSizeChanged(e): void {
        try {
            this.gridComponent['dataSource'] = null;
            this.pageSettings['pageSize'] = e.target.value;
            this.gridComponent['pageSettings'] = this.pageSettings;
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }
    //End Grid Region

    //Search Region
    public onSearchClick(): any {
        try {
            this.updateLocationBySearch();
            this.updatePharmacies();
        }
        catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    onLocationChanged(e): void {
        try {

            if (e === true) {
                if (this.displayMap) {
                    //this.mapComponent.setView(this.locatorComponent.searchLocation.latLng, 13);
                    this.mapComponent.setView(this.searchLocation.latLng, 13);
                }
                else {
                    //no map to populate but need to populate the grid
                    this.updatePharmacies();
                }
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    //onRadiusChanged(e): void {
    //    try {
    //        if (!this.displayMap) {
    //            this.updatePharmacies();
    //        }
    //    } catch (ex) {
    //        this.applicationInsightsLogger.logException(ex);
    //    }
    //}

    //End Search Region

    //Map Region
    onCenterChanged(e): void {
        try {
            if (e === true) {
                this.updatePharmacies();
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    onMarkerClicked(e): void {
        try {
            const location = e['location'];
            this.selectedNabp = location.id;
            const dataSource = this.gridComponent.dataSource as Array<Pharmacy>;
            const sourceIndex = dataSource.findIndex(row => row.nabp === this.selectedNabp);
            const pageNumber = Math.floor((sourceIndex + 1) / 10) + 1;
            this.gridComponent.goToPage(pageNumber);
            setTimeout(() => {
                const rows = this.gridComponent.getCurrentViewRecords() as Array<Pharmacy>;
                const viewIndex = rows.findIndex(row => row.nabp === this.selectedNabp);
                this.gridComponent.selectRow(viewIndex);
            }, 100);
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    onZoomChanged(e): void {
        try {
            if (e === true) {
                this.updatePharmacies();
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }
    //End Map Region
    // endregion

    // region Methods
    //region Grid Methods
    updateGrid() {
        try {

            this.gridComponent['dataSource'] = null;
            this.records = [];

            for (const pharmacy of this.pharmacies) {
                pharmacy['location'] = {
                    address1: pharmacy.address1,
                    address2: pharmacy.address2,
                    city: pharmacy.city,
                    name: pharmacy.name,
                    phone: pharmacy.phone,
                    state: pharmacy.state,
                    zip: pharmacy.zip,
                    directions: pharmacy.directions,

                    distance: pharmacy.distance,

                    alwaysInclude: pharmacy.alwaysInclude,
                    retail: pharmacy.retail,
                    mailOrder: pharmacy.mailOrder,
                    speciality: pharmacy.speciality,
                    //preferred: pharmacy.preferred,
                    preferredOrder: pharmacy.preferredOrder

                };
                pharmacy['pharmacyIndicator'] = this.getPharmacyIndicator(pharmacy.retail, pharmacy.mailOrder, pharmacy.speciality);
                this.records.push(pharmacy);
            }

            this.gridComponent.dataSource = this.records;
            this.content.resize();
        } catch (ex) {
            alert(ex);
            this.applicationInsightsLogger.logException(ex);
        }
    }

    distanceSortComparer(reference, comparer) {
        const sortAsc = action.toLowerCase() === 'ascending' ? true : false;
        let comparison = 0;

        //if both are alwaysIncluded 
        if (reference.alwaysInclude && comparer.alwaysInclude) {
            //compare by the preferred order
            if (reference.preferredOrder > comparer.preferredOrder) {
                return 1;
            }
            else if (reference.preferredOrder < comparer.preferredOrder) {
                return -1;
            }
        }
        //if previous item is alwaysIncluded 
        else if (reference.alwaysInclude && !comparer.alwaysInclude) {
            return - 1;
        }
        //if next item is alwaysIncluded
        else if (!reference.alwaysInclude && comparer.alwaysInclude) {
            return 1;
        }
        //if both items are not alwaysIncluded we should order by the distance
        else if (!reference.alwaysInclude && !comparer.alwaysInclude) {
            //Order by distance
            if (reference.distance > comparer.distance) {
                if (sortAsc)
                    return 1;
                else
                    return -1;
            } else if (reference.distance < comparer.distance) {
                if (sortAsc)
                    return -1;
                else
                    return 1;
            }
        }


        return comparison;
    }

    getPharmacyIndicator(retail, mail, speciality) {

        var pharmacyIndicator = "";

        if (speciality) {
            pharmacyIndicator = "Speciality";
        }
        if (retail) {
            if (pharmacyIndicator.length > 0) {
                pharmacyIndicator = pharmacyIndicator + "/";
            }
            pharmacyIndicator = pharmacyIndicator + "Retail";
        }
        if (mail) {
            if (pharmacyIndicator.length > 0) {
                pharmacyIndicator = pharmacyIndicator + "/";
            }
            pharmacyIndicator = pharmacyIndicator + "Mail Order";
        }
        //if (pharmacyIndicator.length === 0) {
        //    pharmacyIndicator = "Retail";
        //}


        return pharmacyIndicator;
    }

    clearGrid() {
        this.gridComponent['dataSource'] = null;
        this.records = [];
    }

    //end region Grid Methods

    //region Map Methods
    public maxMapDistance: Number = 10;
    public showPharmacyProcessing: boolean = false;
    setupMapSettings() {
        try {
            this.pharmacyLocatorService
                .getClientMapSettings()
                .subscribe((resp) => {
                    const result = JSON.parse(resp.toString());
                    this.displayMap = result.usesPharmacyLocatorMap;
                    this.autoZoom = result.autoZoomOut;
                    this.minPharmacies = result.minimumPharmacies;
                    this.showPharmacyProcessing = result.showPharmacyProcessing;

                    if (!this.displayMap) {
                        if (result.maximumMapDistance >= 5) {
                            let i: number = 5;
                            do {
                                if (i != 5) {
                                    this.radiusData.push({ id: i, value: i });
                                }

                                i += 5;
                            } while (i <= result.maximumMapDistance)
                            this.maxMapDistance = result.maximumMapDistance;
                        }

                    }
                    this.initialLoad = false;
                    this.loaderService.show(false);
                },
                    err => {
                        console.log(err);
                        this.initialLoad = false;
                        this.loaderService.show(false);
                    });
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }

    updateMap() {
        try {
            this.mapComponent.addHomeMarker(this.searchLocation, environment.mapSettings.homeIcon);
            const locations: Location[] = [];

            for (const pharmacy of this.pharmacies) {
                const description = pharmacy.name || pharmacy['description'] || pharmacy['pharmacyName'];

                const location = new Location();
                location['index'] = pharmacy['index'];
                location.id = pharmacy.nabp;
                location.latLng = new L.LatLng(pharmacy.latitude, pharmacy.longitude);
                location['description'] = `${description}`;
                locations.push(location);
            }
            if (this.displayMap) {
                this.mapComponent.updateLocations(locations);
            }
        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
        }
    }
    //end region Map Methods

    //region Pharmacy Methods
    public updatingPharamacies: boolean = false;
    public updatePharmaciesTimer: any = 0;
    
    updatePharmacies() {
        clearTimeout(this.updatePharmaciesTimer);

        if (this.initialLoad)
            return;

        if (this.updatingPharamacies)
            return;

        this.updatePharmaciesTimer =
            setTimeout(() => {
                this.updatePharmaciesCallback();
            }, 500);
    }

    updatePharmaciesCallback() {
        try {


            if (this.initialLoad) {
                this.clearGrid();
                return;
            }

            if (this.updatingPharamacies)
                return;

            this.updatingPharamacies = true;

            setTimeout(() => {
                this.clearGrid();
            }, 0);


            if (this.showPharmacyProcessing) {
                this.loaderService.show(true, 'Retrieving pharmacies');
            }

            if (this.displayMap) {
                const boundingBox = new BoundingBox();

                boundingBox.BottomRight = this.mapComponent.mapBounds.BottomRight;
                boundingBox.UpperLeft = this.mapComponent.mapBounds.UpperLeft;
                const center = this.mapComponent.getCenter();
                boundingBox.StartingLocation = new Geocode(center.lat, center.lng);

                boundingBox.ClientID = '';
                boundingBox.Name = '';
                boundingBox.GroupID = '0';

                this.pharmacyLocatorService
                    .getPharmacyBySquare(boundingBox)
                    .subscribe((resp) => {
                        const result = JSON.parse(resp.toString());
                        if (result.length > 0) {
                            this.pharmacies = result.map((pharmacy, index) => {
                                pharmacy['index'] = index;
                                pharmacy.directions = Pharmacy.getDirections(boundingBox.StartingLocation.Latitude, boundingBox.StartingLocation.Longitude, pharmacy.latitude, pharmacy.longitude);
                                return pharmacy;
                            });
                        } else {
                            this.pharmacies = [];
                        }
                        this.updateMap();
                        this.updateGrid();

                        if (this.autoZoom && this.minPharmacies > this.pharmacies.length) {
                            this.mapComponent.zoomOut();
                            this.updatingPharamacies = false;
                        }
                        else {
                            this.loaderService.show(false);
                            this.updatingPharamacies = false;
                        }

                    },
                        err => {
                            console.log(err);
                            this.loaderService.show(false);
                            this.updatingPharamacies = false;
                        });
            }
            else {

                if (this.searchLocation) {
                    if (this.searchLocation.latLng.lat && this.searchLocation.latLng.lng) {
                        const boundingRadius = new BoundingRadius();
                        boundingRadius.CenterPoint = new Geocode(this.searchLocation.latLng.lat, this.searchLocation.latLng.lng);
                        if (this.defaultRadius) {
                            boundingRadius.Distance = Number(this.defaultRadius);
                        }
                        else {
                            boundingRadius.Distance = 5;
                        }
                        boundingRadius.ClientID = '';
                        boundingRadius.Name = '';
                        boundingRadius.GroupID = '0';

                        this.pharmacyLocatorService
                            .getPharmacyByRadius(boundingRadius)
                            .subscribe((resp) => {
                                const result = JSON.parse(resp.toString());
                                if (result.length > 0) {
                                    this.pharmacies = result.map((pharmacy, index) => {
                                        pharmacy['index'] = index;
                                        pharmacy.directions = Pharmacy.getDirections(boundingRadius.CenterPoint.Latitude, boundingRadius.CenterPoint.Longitude, pharmacy.latitude, pharmacy.longitude);
                                        return pharmacy;
                                    });
                                }
                                else {
                                    this.pharmacies = [];
                                }
                                this.updateGrid();
                                if (this.autoZoom && this.minPharmacies > this.pharmacies.length && Number(this.defaultRadius) < this.maxMapDistance) {
                                    this.defaultRadius = Number(this.defaultRadius) + 5;
                                    this.updatingPharamacies = false;
                                    this.updatePharmacies();
                                }
                                else {
                                    this.loaderService.show(false);
                                    this.updatingPharamacies = false;
                                }
                            },
                                err => {
                                    console.log(err);
                                    this.loaderService.show(false);
                                    this.updatingPharamacies = false;
                                });
                    }
                }
            }


        } catch (ex) {
            this.applicationInsightsLogger.logException(ex);
            this.loaderService.show(false);
            this.updatingPharamacies = false;
        }
    };
    //end region Pharmacy Methods

    // region Methods
    updateLocationToCurrentPosition(): void {
        this.searchAddress = '';
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                this.userLocation = new Location();

                this.userLocation.latLng = new L.LatLng(
                    position.coords.latitude,
                    position.coords.longitude
                );
                this.userLocation.description = 'You are here!';
                this.searchLocation = this.userLocation;
                this.onLocationChanged(true);
            });
        }
    }


    public updateLocationTimer: any = 0;

    updateLocationBySearch(): void {

        clearTimeout(this.updateLocationTimer);

        this.updateLocationTimer =
        setTimeout(() => {
            if (this.searchAddress.length > 4) { // minimum use case would be entering a zip code

                setTimeout(() => {
                    this.clearGrid();
                }, 100);

                this.locatorServiceInterface
                    .getLocationByAddress(this.searchAddress)
                    .subscribe(
                        response => {
                            try {
                                var coords = JSON.parse(response);
                                this.searchLocation = new Location();
                                this.searchLocation.latLng = new L.LatLng(
                                    coords.latitude,
                                    coords.longitude
                                );
                                //this.locationChanged.emit(true);
                                this.onLocationChanged(true);
                                this.error = false;
                            }
                            catch (e) {
                                // do nothing - this is to avoid errors when response is not a valid json object
                            }
                        },
                        err => {
                            if (err !== 'undefined' && err.error && err.error.errorMessages) {
                                this.errorMessage = err.error.errorMessages;
                                this.error = true;
                            }
                        }
                    );
            }
        }, 1000);
    }
    //end region Methods
    // endregion

}



// WEBPACK FOOTER //
// D:/a/29/s/PDMI.PharmacyLocator.Web/src/app/pharmacy-locator/pharmacy-locator.component.ts